import { DateTime } from 'luxon';

import { type DateTimeFieldFormat } from '@/types/schema/fields';

export const sanitizeDateString = (date: string | undefined, fieldFormat: DateTimeFieldFormat) => {
  if (fieldFormat.date_format === 'Ignore Date') {
    return '';
  }

  if (!date || date === '') {
    return '';
  }

  const dateSplit = date.split('/');
  if (dateSplit.length !== 3) {
    return date;
  }

  const middleNumber = parseInt(date.split('/')[1], 10);
  const firstNumber = parseInt(date.split('/')[0], 10);

  if (fieldFormat.date_format === 'dd/mm/yyyy' && middleNumber > 12) {
    return DateTime.fromFormat(date, 'MM/dd/yyyy').toFormat('dd/MM/yyyy');
  }

  if (
    (fieldFormat.date_format === 'mm/dd/yyyy' || fieldFormat.date_format === 'M D, yyyy') &&
    firstNumber > 12
  ) {
    return DateTime.fromFormat(date, 'dd/MM/yyyy').toFormat('MM/dd/yyyy');
  }

  return date;
};

export const sanitizeTimeStringFromHoursMinutes = (
  hours: number | string | undefined,
  minutes: number | string | undefined,
  meridiem: 'AM' | 'PM' | '' | undefined,
  fieldFormat: DateTimeFieldFormat
) => {
  if (!fieldFormat.time_format || fieldFormat.time_format === 'Ignore Time') {
    return '';
  }

  const hasHours = hours !== undefined && hours !== '';
  const hasMinutes = minutes !== undefined && minutes !== '';

  if (!hasHours || !hasMinutes) {
    return '';
  }

  if (fieldFormat.time_format === 'HH:MM am') {
    if (meridiem === 'PM' && Number(hours) < 12) {
      return DateTime.fromObject({
        hour: Number(hours) + 12, // Convert to 24-hour format
        minute: Number(minutes)
      }).toFormat('hh:mma');
    }

    if (meridiem === 'AM' && Number(hours) === 12) {
      return DateTime.fromObject({
        hour: 0, // Convert to 24-hour format
        minute: Number(minutes)
      }).toFormat('hh:mma');
    }

    return DateTime.fromObject({
      hour: Number(hours),
      minute: Number(minutes)
    }).toFormat('hh:mma');
  }

  return DateTime.fromObject({
    hour: Number(hours),
    minute: Number(minutes)
  }).toFormat('HH:mm');
};

export const sanitizeTimeStringToGenericFormat = (
  time: string | undefined,
  timeFormatToUse: 'hh:mma' | 'HH:mm'
) => {
  if (!time || time === '') {
    return '';
  }

  let newTime = time.toUpperCase();
  const timeFormat = newTime.includes('AM') || newTime.includes('PM') ? 'hh:mma' : 'HH:mm';

  // Add a zero to make the time format consistent
  if (timeFormat === 'hh:mma' && time.length === 6) {
    newTime = `0${newTime}`;
  } else if (timeFormat === 'HH:mm' && time.length === 4) {
    newTime = `0${newTime}`;
  }

  if (timeFormat === 'hh:mma' && timeFormatToUse === 'HH:mm') {
    return DateTime.fromFormat(newTime, 'hh:mma').toFormat('HH:mm');
  }

  if (timeFormat === 'HH:mm' && timeFormatToUse === 'hh:mma') {
    return DateTime.fromFormat(newTime, 'HH:mm').toFormat('hh:mma');
  }

  return newTime;
};

export const sanitizeTimeString = (time: string | undefined, fieldFormat: DateTimeFieldFormat) => {
  if (!fieldFormat.time_format || fieldFormat.time_format === 'Ignore Time') {
    return '';
  }

  if (!time || time === '') {
    return '';
  }

  let newTime = time.toUpperCase();
  const timeFormat = newTime?.includes('AM') || newTime?.includes('PM') ? 'hh:mma' : 'HH:mm';

  // Add a zero to make the time format consistent
  if (timeFormat === 'hh:mma' && time?.length === 6) {
    newTime = `0${newTime}`;
  } else if (timeFormat === 'HH:mm' && time.length === 4) {
    newTime = `0${newTime}`;
  }

  if (fieldFormat.time_format === 'HH:MM am' && timeFormat === 'HH:mm') {
    return DateTime.fromFormat(newTime, 'HH:mm').toFormat('hh:mma');
  }

  if (fieldFormat.time_format === 'HH MM (military)' && timeFormat === 'hh:mma') {
    return DateTime.fromFormat(newTime, 'hh:mma').toFormat('HH:mm');
  }

  return newTime;
};

export const sanitizeTimeNumberToString = (
  time: number | undefined,
  fieldFormat: DateTimeFieldFormat
) => {
  if (!fieldFormat.time_format || fieldFormat.time_format === 'Ignore Time') {
    return '';
  }

  if (time === undefined) {
    return '';
  }

  // When the time is a number, it represents the number of minutes after midnight, so we need to convert it to hours and minutes
  const hours = Math.floor(time / 60);
  const minutes = time % 60;

  if (hours < 0 || minutes < 0 || hours > 23 || minutes > 59) {
    return '';
  }

  if (fieldFormat.time_format === 'HH:MM am') {
    let displayHours = hours;
    if (hours === 0) {
      displayHours = 12;
    } else if (hours > 12) {
      displayHours = hours - 12;
    }

    const amPm = hours < 12 ? 'AM' : 'PM';
    return `${displayHours < 10 ? `0${displayHours}` : displayHours}:${minutes < 10 ? `0${minutes}` : minutes}${amPm}`;
  }

  if (fieldFormat.time_format === 'HH MM (military)') {
    return `${hours < 10 ? `0${hours}` : hours}:${minutes < 10 ? `0${minutes}` : minutes}`;
  }

  return '';
};

export const getNormalizedDefaultTimeFormat = (time: string, fieldFormat: DateTimeFieldFormat) => {
  let format = 'HH:mm';

  if (!fieldFormat) {
    return format;
  }

  if (fieldFormat.time_format === 'HH:MM am') {
    // The V3 saves the value without leading zeros, so we need to adjust the format
    // Ex.: 1:32AM
    format = time.length === 6 ? 'h:mma' : 'hh:mma';
  }

  return format;
};
